import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { siteService, SiteLanguage } from '../api/site';

interface SiteState {
  config: any;
  wheelPrizes: any[] | null;
  isLoading: boolean;
  error: string | null;
  languages: SiteLanguage[];
  defaultLanguage: string;
  isLanguagesLoading: boolean;
  hasFetchedLanguages: boolean;
}

const initialState: SiteState = {
  config: null,
  wheelPrizes: null,
  isLoading: false,
  error: null,
  languages: [],
  defaultLanguage: 'en',
  isLanguagesLoading: false,
  hasFetchedLanguages: false,
};

export const fetchSiteConfig = createAsyncThunk(
  'site/fetchConfig',
  async (_, { rejectWithValue }) => {
    try {
      const response = await siteService.getSiteConfig();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch site config');
    }
  },
);

export const fetchWheelPrizes = createAsyncThunk(
  'site/fetchWheelPrizes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await siteService.getSiteConfig();
      return response.data?.siteWheel?.prizes || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch wheel prizes');
    }
  },
);

export const fetchGeoStatus = createAsyncThunk(
  'site/fetchGeoStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await siteService.getSiteConfig();
      return response.data?.geo;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch geo status');
    }
  },
);

export const fetchSiteLanguages = createAsyncThunk(
  'site/fetchLanguages',
  async (_, { rejectWithValue }) => {
    try {
      const response = await siteService.getLanguages();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch languages');
    }
  },
);

const siteSlice = createSlice({
  name: 'site',
  initialState,
  reducers: {
    updateSiteWheelPrizes: (state, action: PayloadAction<any>) => {
      state.wheelPrizes = Array.isArray(action.payload)
        ? action.payload
        : (action.payload?.prizes ?? action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSiteConfig.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSiteConfig.fulfilled, (state, action) => {
        state.isLoading = false;
        state.config = action.payload;
      })
      .addCase(fetchSiteConfig.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchWheelPrizes.fulfilled, (state, action) => {
        state.wheelPrizes = action.payload;
      })
      .addCase(fetchGeoStatus.fulfilled, (state, action) => {
        if (state.config) {
          state.config.geo = action.payload;
        }
      })
      .addCase(fetchSiteLanguages.pending, (state) => {
        state.isLanguagesLoading = true;
      })
      .addCase(fetchSiteLanguages.fulfilled, (state, action) => {
        state.isLanguagesLoading = false;
        state.hasFetchedLanguages = true;
        if (action.payload) {
          state.languages = action.payload.languages || [];
          state.defaultLanguage = action.payload.defaultLanguage || 'en';
        }
      })
      .addCase(fetchSiteLanguages.rejected, (state) => {
        state.isLanguagesLoading = false;
        state.hasFetchedLanguages = true;
      });
  },
});

export const { updateSiteWheelPrizes } = siteSlice.actions;

export default siteSlice.reducer;
